import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import _ from 'lodash';
import { Box, List, ListItem, Grid, Typography, Fab, Divider } from "@material-ui/core";
import { SectionAlternate, CardBase, Hero } from "@components/organisms";
import { DialogAlert } from '@components/molecules';
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faQuestionCircle, faClock, faTimesCircle, faPlusCircle, faArrowLeft } from "@fortawesome/free-solid-svg-icons";

//components
import { ViewCertificates, AddCertificates } from './components';
import { useDispatch, useSelector } from "react-redux";

// redux functions
import { saveEmployeeProfile } from "@store/credentials";
//API Requests
import { CredentialsService } from "@services";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    width: "100%",
  },
  section: {
    "& .section-alternate__content": {
      paddingTop: 0,
      marginTop: theme.spacing(-5),
      position: "relative",
      zIndex: 1,
    },
    "& .card-base__content": {
      padding: theme.spacing(2),
      [theme.breakpoints.up("md")]: {
        padding: theme.spacing(3),
      },
    },
  },
  menu: {
    height: "auto",
  },
  list: {
    display: "inline-flex",
    overflow: "auto",
    flexWrap: "nowrap",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "column",
      marginRight: theme.spacing(-3),
      marginLeft: theme.spacing(-3),
    },
  },
  listItem: {
    marginRight: theme.spacing(2),
    flex: 0,
    [theme.breakpoints.up("md")]: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      borderLeft: "2px solid transparent",
    },
  },
  listItemActive: {
    [theme.breakpoints.up("md")]: {
      borderLeft: `2px solid ${theme.palette.primary.dark}`,
    },
    "& .menu__item": {
      color: theme.palette.text.primary,
    },
  },
  box: {
    width: '100%',
  },
  ledgerContainer: {
    padding: theme.spacing(2),
    background: theme.palette.background.subHeader,
    borderRadius: theme.spacing(0.5),
  },
  CURRENT: {
    color: theme.palette.icon.current,
  },
  EXPIRED: {
    color: theme.palette.icon.expired,
  },
  MISSING: {
    color: theme.palette.icon.missing,
  },
  PENDING: {
    color: theme.palette.icon.pending,
  },
  certificateContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
    color: '#004987'

  },
  ledgerTitle: {
    paddingLeft: theme.spacing(1),
  },
  flex: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  flexCenter: {
    justifyContent: 'center',
  },
  plusIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.5rem',
    cursor: 'pointer',
  },
  leftArrowIcon: {
    width: '25px',
    height: '25px',
    minHeight: '25px',
  }
}));

const HeaderPanel = (props) => {
  const { value, index, classes, handleClickIndex, ...other } = props;

  return (
    <Box component="div" {...other} className={classes.box}>
      {
        !index ? (
          <Grid container flex className={classes.ledgerContainer} justifyContent="space-between" >
            <Grid item xs={11} className={classes.flex}>
              <Grid item xs={12} sm={3} className={classes.certificateContainer}>
                <Typography variant="body2" color="primary" style={{ fontWeight: 'bold' }}>
                  VIEW CREDENTIALS
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2} className={classes.certificateContainer}>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="lg"
                  className={classes.CURRENT}
                />
                <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                  Current
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2} className={classes.certificateContainer}>
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  size="lg"
                  className={classes.EXPIRED}
                />
                <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                  Expired
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2} className={classes.certificateContainer}>
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  size="lg"
                  className={classes.MISSING}
                />
                <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                  Missing
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2} className={classes.certificateContainer}>
                <FontAwesomeIcon
                  icon={faClock}
                  size="lg"
                  className={classes.PENDING}
                />
                <Typography variant="body2" color="textPrimary" className={classes.ledgerTitle}>
                  Pending
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={1} sm={1} className={classes.flex} justifyContent="space-evenly">
              <FontAwesomeIcon
                icon={faPlusCircle}
                size="lg"
                className={classes.plusIcon}
                onClick={() => handleClickIndex(1)}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container flex className={classes.ledgerContainer} justifyContent="space-between">
            <Fab color="primary" aria-label="left arrow" size="small" className={classes.leftArrowIcon}>
              <FontAwesomeIcon
                icon={faArrowLeft}
                size="sm"
                className={classes.flexCenter}
                onClick={() => handleClickIndex(0)}
              />
            </Fab>
          </Grid>
        )
      }
      <Divider />
    </Box>
  );
};

const TabPanel = (props) => {
  const { children, value, index, classes, ...other } = props;
  // console.log('value', value, 'index', index);
  return (
    <Box component="div" hidden={value !== index} {...other} className={classes.box}>
      {value === index && children}
    </Box>
  );
};

const Credentials = (props = {}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { user } = useSelector(state => state.user);
  const dispatch = useDispatch();

  //Set variable
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [addCertClearDataAlert, setAddCertClearDataAlert] = useState(false);
  const addCertificateRef = useRef(null);

  useEffect(() => {
    //debugger;
    setTimeout(() =>
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      }), 800
    );
  }, []);

  const handleClickIndex = (newValue) => {

    if (selectedIndex === 1) {
      const formikObj = addCertificateRef.current.getFormikObject();
      const values = formikObj.values;
      const touched = formikObj.touched;
      let fieldTouchAndEnterInput = false;

      _.keys(touched).forEach(touchField => {
        if (touched[touchField] && values[touchField] !== "") {
          fieldTouchAndEnterInput = true;
          return false;
        }
      });
      if (fieldTouchAndEnterInput) {
        setAddCertClearDataAlert(true);
        return;
      }
    }
    setSelectedIndex(newValue);
  };

  // //onPageLoad - fetch employee info - SAMPLE API Call
  // useEffect(() => {
  //   setLoading(true);
  //   // let employeeNum = "100023"; //Brian	L.	Martin's employee number
  //   //sample api GET request
  //   CredentialsService.getemployeeProfile(user.employeeNumber)
  //     .then((data) => {
  //       console.log('employee data: ', data)
  //       setLoading(false);
  //       dispatch(saveEmployeeProfile(data[0]));
  //     })
  //     .catch((err) => {
  //       alert(JSON.stringify(err));
  //       setLoading(false);
  //     });
  // }, []);

  const onAddCertAlertYesClickHandler = () => {
    setAddCertClearDataAlert(false);
    setSelectedIndex(0);
  };

  const onAddCertAlertNoClickHandler = () => {
    setAddCertClearDataAlert(false);
  };

  return (
    <div className={classes.root}>
      <Hero title={"Credentials"} subtitle={""} />
      <SectionAlternate className={classes.section}>
        <Paper className={classes.root}>
          {/* <Tabs
            value={selectedIndex}
            onChange={handleChangeIndex}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="auto"
            centered
          >
            <Tab label="View Credentials" />
            <Tab label="Add Credentials" />
          </Tabs> */}
          <HeaderPanel value={0} index={selectedIndex} classes={classes} handleClickIndex={handleClickIndex} />
        </Paper>
        <Grid container spacing={4}>
          <Grid item xs={12} md={12}>
            <CardBase withShadow align="left">
              <TabPanel value={0} index={selectedIndex} classes={classes}><ViewCertificates /></TabPanel>
              <TabPanel value={1} index={selectedIndex} classes={classes}><AddCertificates ref={addCertificateRef} /></TabPanel>
            </CardBase>
          </Grid>
        </Grid>
        <DialogAlert {...{
          dialogProps: {
            open: addCertClearDataAlert,
            classes: { paper: classes.dialog }
          },
          title: 'All the fields will be cleared? Do you wish to continue.',
          onYesClickHandler: () => onAddCertAlertYesClickHandler(),
          onNoClickHandler: () => onAddCertAlertNoClickHandler()
        }}
        />
      </SectionAlternate>
    </div>
  );
};

export default Credentials;
